import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Snackbar,
    Stack,
    Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import { useAuth } from 'react-oidc-context';
import CreateApi from '../../../shared/Settings/appSettings';
import { useTBContext } from '../../Contexts/useTBStates';
import { CardTemplateDesigner } from './CardTemplateDesigner';
import { ScoreCodeCardItem } from './ScoreCodeCardItem';
import { useScoreCodeCardPDF } from './useScoreCodeCardPDF';
import type { CardTemplate } from './scoreCardTypes';
import { DEFAULT_CARD_TEMPLATE } from './scoreCardTypes';

// Mirrors TeeSheetDisplay palette for consistent player-management sections.
const PRIMARY = {
    main: 'rgba(30, 78, 55, 1)',
} as const;

const SEPIA = {
    bg: 'rgb(253, 252, 248)',
    light: 'rgb(189, 148, 98)',
} as const;

interface ScoreCodeCardsProps {
    registrations: Record<string, unknown>[];
    teeTimes: string[];
    roundValue: number;
    viewEvent: Record<string, unknown> | null;
    factor?: number;
}

export const ScoreCodeCards: React.FC<ScoreCodeCardsProps> = ({
    registrations,
    teeTimes,
    roundValue,
    viewEvent,
    factor = 1,
}) => {
    const auth = useAuth();
    const api  = CreateApi();
    const { setViewEvent, setEventData, eventData } = useTBContext();

    const [template, setTemplate]           = useState<CardTemplate>(DEFAULT_CARD_TEMPLATE);
    const [designerOpen, setDesignerOpen]   = useState(false);
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const [saving, setSaving]               = useState(false);
    const [snack, setSnack]                 = useState<string | null>(null);

    const { generatePDF } = useScoreCodeCardPDF();

    // ── Hydrate template whenever a new event is selected ─────────────────────
    useEffect(() => {
        const savedTemplate = (viewEvent as any)?.scoreCardTemplate;
        setTemplate(savedTemplate ?? DEFAULT_CARD_TEMPLATE);
    }, [(viewEvent as any)?._id]);

    const activeRegs = useMemo(
        () => registrations.filter(
            (r: any) => !r.canceled && !r.withdrawn && !r.disqualified && !r.refunded,
        ),
        [registrations],
    );

    // Group registrations by scoreCode so team formats get one card per scoring unit.
    // The playerName field on the merged record joins all names gracefully.
    const mergedRegs = useMemo<Record<string, unknown>[]>(() => {
        const groups = new Map<string, Record<string, unknown>[]>();
        for (const reg of activeRegs) {
            const key = String((reg as any).scoreCode ?? (reg as any)._id ?? '');
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(reg);
        }
        return Array.from(groups.values()).map((group) => {
            const names = group
                .map((r: any) => r.playerName)
                .filter(Boolean) as string[];
            return { ...(group[0] as any), playerName: names.join(' / ') };
        });
    }, [activeRegs]);

    const previewRegistration: Record<string, unknown> = mergedRegs[0] ?? {};

    // ── Upload an image file to blob storage; returns the CDN URL ─────────────
    const handleImageUpload = async (file: File): Promise<string> => {
        const eventId = (viewEvent as any)?._id;
        if (!eventId) throw new Error('No event selected');

        const formData = new FormData();
        formData.append('image', file);
        formData.append('_id', eventId);
        formData.append('userID', auth.user?.profile.sub ?? '');

        const response = await api.post('/image/uploadEventLogo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.status !== 200) throw new Error('Upload failed');
        return response.data.imageUrl as string;
    };

    // ── Save template to the event document ───────────────────────────────────
    const handleSaveTemplate = async (savedTemplate: CardTemplate) => {
        setTemplate(savedTemplate);
        setDesignerOpen(false);

        const eventId = (viewEvent as any)?._id;
        if (!eventId) return;

        setSaving(true);
        try {
            await api.post('/tb/UpdateEvent', {
                _id: eventId,
                userID: auth.user?.profile.sub,
                scoreCardTemplate: savedTemplate,
            });

            // Sync the updated template into context so it survives panel switches
            const updatedEvent = { ...(viewEvent as any), scoreCardTemplate: savedTemplate };
            setViewEvent(updatedEvent);
            setEventData(
                (eventData as any[]).map((e: any) =>
                    e._id === eventId ? { ...e, scoreCardTemplate: savedTemplate } : e,
                ),
            );

            setSnack('Template saved to event.');
        } catch (err) {
            console.error('Error saving score card template:', err);
            setSnack('Failed to save template — changes kept locally.');
        } finally {
            setSaving(false);
        }
    };

    const handleGeneratePDF = async () => {
        setPdfGenerating(true);
        try {
            await generatePDF({ registrations: mergedRegs, template, viewEvent, roundValue });
        } finally {
            setPdfGenerating(false);
        }
    };

    return (
        <Stack
            direction="column"
            sx={{
                width: '100%',
                maxWidth: 1200,
                flex: 1,
                alignSelf: 'stretch',
                minHeight: 0,
                border: `1.5px solid ${SEPIA.light}`,
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: SEPIA.bg,
                boxShadow: '0 2px 10px rgba(0,0,0,0.10)',
                boxSizing: 'border-box',
            }}
        >
            {/* Header bar — stays visible while grid scrolls */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                sx={{
                    flexShrink: 0,
                    backgroundColor: SEPIA.bg,
                    px: 2,
                    py: 1.25,
                    gap: 1,
                    borderBottom: `1px solid ${SEPIA.light}`,
                }}
            >
                <Typography
                    sx={{
                        fontSize: 16 * factor,
                        fontWeight: 700,
                        color: PRIMARY.main,
                        letterSpacing: '0.04em',
                        flexShrink: 0,
                    }}
                >
                    Score Code Cards
                </Typography>

                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PaletteOutlinedIcon sx={{ fontSize: `${14 * factor}px !important` }} />}
                        onClick={() => setDesignerOpen(true)}
                        sx={{
                            fontSize: 12 * factor,
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1.5,
                        }}
                    >
                        Design Template
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                            pdfGenerating
                                ? <CircularProgress size={14} />
                                : <DownloadIcon sx={{ fontSize: `${14 * factor}px !important` }} />
                        }
                        disabled={pdfGenerating || mergedRegs.length === 0}
                        onClick={handleGeneratePDF}
                        sx={{
                            fontSize: 12 * factor,
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1.5,
                        }}
                    >
                        {pdfGenerating ? 'Generating PDF…' : 'Print PDF'}
                    </Button>
                    {saving && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <CircularProgress size={12} />
                            <Typography variant="caption" color="text.secondary">Saving…</Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>

            {/* Summary strip — outside scroll */}
            <Stack
                direction="row"
                alignItems="center"
                sx={{
                    flexShrink: 0,
                    px: 2,
                    py: 0.75,
                    borderBottom: `1px solid ${SEPIA.light}`,
                    backgroundColor: SEPIA.bg,
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    {mergedRegs.length} score code{mergedRegs.length !== 1 ? 's' : ''}
                    {mergedRegs.length > 0 &&
                        ` · ${Math.ceil(mergedRegs.length / 8)} page${Math.ceil(mergedRegs.length / 8) !== 1 ? 's' : ''} (8 per page)`}
                </Typography>
            </Stack>

            {/* Scrollable card grid */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    px: 2,
                    py: 2,
                }}
            >
                {activeRegs.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 180,
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            No active registrations for this event.
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: 2,
                        }}
                    >
                        {mergedRegs.map((reg: any) => (
                            <Stack key={reg._id ?? reg.scoreCode} alignItems="center" spacing={0.5}>
                                <ScoreCodeCardItem
                                    template={template}
                                    registration={reg}
                                    roundValue={roundValue}
                                    eventData={(viewEvent ?? undefined) as Record<string, unknown> | undefined}
                                    displayWidth={200}
                                    showBorder
                                />
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {reg.playerName ?? reg.scoreCode ?? '—'}
                                </Typography>
                            </Stack>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Template designer dialog */}
            <CardTemplateDesigner
                open={designerOpen}
                template={template}
                previewRegistration={previewRegistration}
                previewEventData={(viewEvent ?? undefined) as Record<string, unknown> | undefined}
                roundValue={roundValue}
                onImageUpload={handleImageUpload}
                onSave={handleSaveTemplate}
                onClose={() => setDesignerOpen(false)}
            />

            {/* Save feedback snackbar */}
            <Snackbar
                open={snack !== null}
                autoHideDuration={3500}
                onClose={() => setSnack(null)}
                message={snack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Stack>
    );
};
